import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/integrations/supabase/client'
import { SectionHero, AdminCard, StatGrid, MetricCard, StatusPill } from '@/components/admin/primitives'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Search, Download, User, AlertTriangle, GraduationCap, FileText } from 'lucide-react'
import { toast } from 'sonner'
import { useAdminAccess } from '@/hooks/useAdminAccess'
import { searchLearningUser, getUserLearningStages, bulkUpdateLearningStages, getLearningDistribution, type LearningStage, type LearningUser } from '@/lib/api/admin/learning'
import { formatDate, formatDateTime } from '@/lib/time/formatDate'

export default function LearningAdminPage() {
  const { isPlatformAdmin } = useAdminAccess()
  const queryClient = useQueryClient()
  const [searchInput, setSearchInput] = useState('')
  const [searchType, setSearchType] = useState<'email' | 'user_id'>('email')
  const [selectedUser, setSelectedUser] = useState<LearningUser | null>(null)
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null)

  const { data: stagesData, isLoading: stagesLoading, refetch: refetchStages } = useQuery({
    queryKey: ['admin-learning-stages', selectedUserId],
    queryFn: () => {
      if (!selectedUserId) throw new Error('User ID required')
      return getUserLearningStages(selectedUserId)
    },
    enabled: !!selectedUserId,
  })

  const { data: distributionData, isLoading: distributionLoading } = useQuery({
    queryKey: ['admin-learning-distribution', selectedUserId],
    queryFn: () => {
      if (!selectedUserId) throw new Error('User ID required')
      return getLearningDistribution(selectedUserId)
    },
    enabled: !!selectedUserId,
  })

  const { data: auditLogs, isLoading: auditLoading } = useQuery({
    queryKey: ['admin-audit-learning', selectedUserId],
    queryFn: async () => {
      if (!selectedUserId) return { items: [] }
      const { data, error } = await supabase
        .from('admin_audit')
        .select('*')
        .eq('target_type', 'user_learning_stages')
        .eq('target_id', selectedUserId)
        .order('created_at', { ascending: false })
        .limit(20)
      if (error) throw error
      return { items: data || [] }
    },
    enabled: !!selectedUserId && isPlatformAdmin,
  })

  const updateMutation = useMutation({
    mutationFn: ({ drillId, stage }: { drillId: string; stage: LearningStage['learning_stage'] }) => {
      if (!selectedUserId) throw new Error('User ID required')
      return bulkUpdateLearningStages({
        user_id: selectedUserId,
        updates: [{ drill_id: drillId, learning_stage: stage }],
        reason: 'Manual update via admin interface',
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-learning-stages', selectedUserId] })
      queryClient.invalidateQueries({ queryKey: ['admin-learning-distribution', selectedUserId] })
      queryClient.invalidateQueries({ queryKey: ['admin-audit-learning', selectedUserId] })
      toast.success('Learning stage updated')
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to update learning stage')
    },
  })

  const handleSearch = async () => {
    if (!searchInput.trim()) {
      toast.error(`Please enter a ${searchType === 'email' ? 'user email' : 'user ID (UUID)'}`)
      return
    }

    try {
      let userData: { user: LearningUser }
      
      if (searchType === 'email') {
        userData = await searchLearningUser(searchInput.trim())
      } else {
        // Validate UUID format
        const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
        if (!uuidRegex.test(searchInput.trim())) {
          toast.error('Please enter a valid UUID')
          return
        }
        userData = await searchLearningUser(undefined, searchInput.trim())
      }

      setSelectedUser(userData.user)
      setSelectedUserId(userData.user.id)
      toast.success(`Found user: ${userData.user.email}`)
    } catch (error: any) {
      console.error('Error searching user:', error)
      toast.error(error.message || 'Failed to find user')
    }
  }

  const handleExportCSV = () => {
    if (!stagesData?.stages || stagesData.stages.length === 0) {
      toast.error('No data to export')
      return
    }

    const headers = ['Drill ID', 'Drill Title', 'Learning Stage', 'Confidence Score', 'Assessment Date', 'Last Updated']
    const rows = stagesData.stages.map(stage => [
      stage.drill_id,
      stage.drill?.title || '',
      stage.learning_stage,
      stage.confidence_score ? (stage.confidence_score * 100).toFixed(0) + '%' : '',
      stage.assessment_date ? formatDate(stage.assessment_date) : '',
      formatDate(stage.updated_at),
    ])

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n')

    const blob = new Blob([csvContent], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `learning-stages-${selectedUserId}-${new Date().toISOString().split('T')[0]}.csv`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
    
    toast.success('CSV exported successfully')
  }

  const stages = stagesData?.stages || []
  const distribution = distributionData?.distribution || { cognitive: 0, associative: 0, autonomous: 0, total: 0 }

  return (
    <div className="min-h-screen bg-surface pb-20">
      <div className="max-w-admin mx-auto px-admin-content-padding py-8">
        {/* Hero Section */}
        <SectionHero
          title="Learning Stages Admin"
          description="Manage user learning progression and drill stages"
          gradient="primary"
        />

        {/* User Search */}
        <div className="mt-8">
          <AdminCard title="Search User" accentColor="info">
            <div className="space-y-4">
              <Alert>
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>
                  Search by email or user ID (UUID) to view and manage learning stages.
                </AlertDescription>
              </Alert>
              <div className="flex gap-2">
                <Select value={searchType} onValueChange={(v) => setSearchType(v as 'email' | 'user_id')}>
                  <SelectTrigger className="w-32">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="email">Email</SelectItem>
                    <SelectItem value="user_id">User ID</SelectItem>
                  </SelectContent>
                </Select>
                <div className="flex-1">
                  <Label htmlFor="search-input">
                    {searchType === 'email' ? 'Email' : 'User ID (UUID)'}
                  </Label>
                  <Input
                    id="search-input"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    placeholder={searchType === 'email' ? 'user@example.com' : '00000000-0000-0000-0000-000000000000'}
                    onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  />
                </div>
                <div className="flex items-end">
                  <Button onClick={handleSearch}>
                    <Search className="w-4 h-4 mr-2" />
                    Search
                  </Button>
                </div>
              </div>
              {selectedUser && (
                <div className="p-3 bg-muted rounded-lg">
                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4 text-muted-foreground" />
                    <div>
                      <p className="font-medium">{selectedUser.email}</p>
                      <p className="text-xs text-muted-foreground">ID: {selectedUser.id}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </AdminCard>
        </div>

        {/* Stage Distribution */}
        {selectedUserId && (
          <div className="mt-8">
            <h2 className="text-admin-section font-semibold text-foreground mb-4">Stage Distribution</h2>
            {distributionLoading ? (
              <StatGrid columns={3}>
                {[1, 2, 3].map((i) => (
                  <div key={i} className="h-32 bg-muted animate-pulse rounded-lg" />
                ))}
              </StatGrid>
            ) : (
              <StatGrid columns={3}>
                <MetricCard
                  title="Cognitive"
                  value={distribution.cognitive}
                  description={`${distributionData?.percentages.cognitive.toFixed(1) || 0}%`}
                  size="large"
                  accentColor="info"
                />
                <MetricCard
                  title="Associative"
                  value={distribution.associative}
                  description={`${distributionData?.percentages.associative.toFixed(1) || 0}%`}
                  size="large"
                  accentColor="warning"
                />
                <MetricCard
                  title="Autonomous"
                  value={distribution.autonomous}
                  description={`${distributionData?.percentages.autonomous.toFixed(1) || 0}%`}
                  size="large"
                  accentColor="success"
                />
              </StatGrid>
            )}
          </div>
        )}

        {/* Learning Stages Table */}
        {selectedUserId && (
          <div className="mt-8">
            <Tabs defaultValue="stages" className="space-y-4">
              <div className="flex items-center justify-between">
                <TabsList>
                  <TabsTrigger value="stages">Learning Stages</TabsTrigger>
                  <TabsTrigger value="audit">Audit Log</TabsTrigger>
                </TabsList>
                {stages.length > 0 && (
                  <Button onClick={handleExportCSV} variant="outline" size="sm">
                    <Download className="w-4 h-4 mr-2" />
                    Export CSV
                  </Button>
                )}
              </div>

              <TabsContent value="stages">
                <AdminCard title="Learning Stages" accentColor="primary">
                  {stagesLoading ? (
                    <p className="text-muted-foreground">Loading...</p>
                  ) : stages.length > 0 ? (
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Drill</TableHead>
                            <TableHead>Current Stage</TableHead>
                            <TableHead>Confidence</TableHead>
                            <TableHead>Last Updated</TableHead>
                            <TableHead>Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {stages.map((stage) => (
                            <TableRow key={stage.drill_id}>
                              <TableCell>{stage.drill?.title || stage.drill_id}</TableCell>
                              <TableCell>
                                <StatusPill
                                  status={
                                    stage.learning_stage === 'autonomous'
                                      ? 'approved'
                                      : stage.learning_stage === 'associative'
                                      ? 'pending'
                                      : 'critical'
                                  }
                                  label={stage.learning_stage}
                                />
                              </TableCell>
                              <TableCell>
                                {stage.confidence_score
                                  ? `${(stage.confidence_score * 100).toFixed(0)}%`
                                  : 'N/A'}
                              </TableCell>
                              <TableCell>
                                {stage.updated_at ? formatDate(stage.updated_at) : 'N/A'}
                              </TableCell>
                              <TableCell>
                                <Select
                                  value={stage.learning_stage}
                                  onValueChange={(value) =>
                                    updateMutation.mutate({ drillId: stage.drill_id, stage: value as LearningStage['learning_stage'] })
                                  }
                                  disabled={updateMutation.isPending}
                                >
                                  <SelectTrigger className="w-32">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="cognitive">Cognitive</SelectItem>
                                    <SelectItem value="associative">Associative</SelectItem>
                                    <SelectItem value="autonomous">Autonomous</SelectItem>
                                  </SelectContent>
                                </Select>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  ) : (
                    <p className="text-muted-foreground">No learning stages found for this user</p>
                  )}
                </AdminCard>
              </TabsContent>

              <TabsContent value="audit">
                <AdminCard title="Audit Log" accentColor="info">
                  {auditLoading ? (
                    <p className="text-muted-foreground">Loading...</p>
                  ) : !auditLogs || auditLogs.items.length === 0 ? (
                    <p className="text-muted-foreground">No audit logs found</p>
                  ) : (
                    <div className="space-y-2">
                      {auditLogs.items.map((log: any) => (
                        <div key={log.id} className="p-3 border rounded-lg">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <p className="font-medium">{log.action} {log.target_type}</p>
                              {log.reason && (
                                <p className="text-sm text-muted-foreground mt-1">Reason: {log.reason}</p>
                              )}
                              {log.metadata && (
                                <p className="text-xs text-muted-foreground mt-1">
                                  {JSON.stringify(log.metadata, null, 2)}
                                </p>
                              )}
                            </div>
                            <p className="text-xs text-muted-foreground">
                              {formatDateTime(log.created_at)}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </AdminCard>
              </TabsContent>
            </Tabs>
          </div>
        )}
      </div>
    </div>
  )
}

